import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Shield } from "lucide-react";
import { getAppVersion } from "../config/version";

const Landing = ({ onAuthSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Emergency backdoor: Add ?emergency=sto2024 to URL to show internal login
  const [showEmergencyLogin, setShowEmergencyLogin] = useState(false);

  useEffect(() => {
    // Check for emergency access parameter
    const urlParams = new URLSearchParams(window.location.search);
    const emergencyKey = urlParams.get('emergency');
    if (emergencyKey === 'sto2024') {
      setShowEmergencyLogin(true);
      // Delay URL cleanup to avoid race condition with React state
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 100);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      // Manually encode parameters to handle special characters in password
      const encodedEmail = encodeURIComponent(email);
      const encodedPassword = encodeURIComponent(password);
      const response = await axiosInstance.post(
        `/auth/login?email=${encodedEmail}&password=${encodedPassword}`
      );
      // Success - pass user to parent (loading state will be cleared by unmount)
      onAuthSuccess(response.data.user);
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Échec de connexion");
    } finally {
      // Always reset loading state
      setLoading(false);
    }
  };

  const handleSSOLogin = async () => {
    try {
      setSsoLoading(true);
      const response = await axiosInstance.get("/auth/sso-login");
      if (response.data.auth_url) {
        // Redirect to Microsoft login
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      console.error("SSO login error:", error);
      toast.error("Erreur lors de l'initialisation SSO");
      setSsoLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center">
      <div className="max-w-md mx-auto px-8">
        <img
          src="/logo.png"
          alt="Logo"
          className="mx-auto mb-8 max-w-xs w-full"
          data-testid="landing-logo"
        />
        <h1
          className="text-4xl font-bold text-white mb-8 text-center"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          data-testid="landing-title"
        >
          Gestion MSP STO
        </h1>
        
        <div className="space-y-4 bg-neutral-800 p-8 rounded-xl border border-neutral-700">
          {/* Microsoft SSO Button - Primary and Only Login Method */}
          <Button
            onClick={handleSSOLogin}
            disabled={ssoLoading}
            className="w-full bg-[#0078d4] hover:bg-[#106ebe] text-white py-4 text-lg font-semibold rounded-lg flex items-center justify-center gap-3"
            data-testid="sso-login-button"
          >
            {ssoLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="10" height="10" fill="#f25022"/>
                <rect x="11" width="10" height="10" fill="#7fba00"/>
                <rect y="11" width="10" height="10" fill="#00a4ef"/>
                <rect x="11" y="11" width="10" height="10" fill="#ffb900"/>
              </svg>
            )}
            {ssoLoading ? "Connexion..." : "Se connecter avec Microsoft"}
          </Button>
          
          <p className="text-xs text-neutral-500 text-center">
            Connectez-vous avec votre compte Microsoft Entra ID
          </p>
          
          {/* Emergency Login Form - Only visible with secret URL parameter */}
          {showEmergencyLogin && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-orange-600/50"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-neutral-800 text-orange-400 flex items-center gap-1">
                    <Shield size={14} />
                    Accès d&apos;urgence
                  </span>
                </div>
              </div>
              
              <form onSubmit={handleLogin} className="space-y-4 pt-2 border-t border-orange-600/30 bg-orange-900/10 -mx-8 px-8 pb-4 -mb-4 rounded-b-xl">
                <p className="text-orange-400 text-xs text-center mb-2">
                  Mode de secours - Utiliser uniquement si SSO ne fonctionne pas
                </p>
                <div>
                  <Label className="text-white text-sm">Identifiant administrateur</Label>
                  <Input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-neutral-900 border-orange-700/50 text-white"
                    placeholder="admin"
                    data-testid="login-email"
                  />
                </div>
                <div>
                  <Label className="text-white text-sm">Mot de passe</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-neutral-900 border-orange-700/50 text-white"
                    placeholder="••••••"
                    data-testid="login-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-700 hover:bg-orange-600 text-white py-2 font-medium rounded-lg"
                  data-testid="login-button"
                >
                  {loading ? "Connexion..." : "Connexion d'urgence"}
                </Button>
              </form>
            </>
          )}
        </div>
        
        <p className="text-xs text-neutral-500 mt-8 text-center">
          Version {getAppVersion()} © Dominic Dussault
        </p>
      </div>
    </div>
  );
};

export default Landing;